import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { CkEditorField } from '../../components/ui/CkEditorField';
import { SearchableSelect, type SearchableSelectOption } from '../../components/ui/SearchableSelect';
import { ApiError } from '../../lib/api-client';
import { useToast } from '../../lib/toast';
import { fetchSettingsLookup } from './settings.api';
import { settingsKeys, useSettingsLookup } from './settings.queries';
import { getReferenceId, lookupRecordLabel } from './settings.utils';
import type { SettingsRecord } from './types';

interface EditorProps {
  record?: SettingsRecord | null;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}

const textValue = (value: unknown) => typeof value === 'string' ? value : '';
const numberValue = (value: unknown) => typeof value === 'number' ? String(value) : '';
const optionalText = (value: string) => value.trim() || null;

const Drawer = ({ title, eyebrow, isPending, onClose, children, onSubmit }: {
  title: string;
  eyebrow: string;
  isPending: boolean;
  onClose: () => void;
  children: ReactNode;
  onSubmit: (event: FormEvent) => void;
}) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !isPending) onClose(); };
    document.body.classList.add('modal-open');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isPending, onClose]);

  return (
    <div className="settings-drawer-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isPending) onClose();
    }}>
      <aside className="settings-drawer settings-drawer-wide" role="dialog" aria-modal="true" aria-labelledby="specialized-editor-title">
        <header className="settings-drawer-header">
          <div><p className="eyebrow">{eyebrow}</p><h2 id="specialized-editor-title">{title}</h2><p>Required fields are marked with an asterisk.</p></div>
          <button className="settings-drawer-close" type="button" onClick={onClose} disabled={isPending} aria-label="Close editor">x</button>
        </header>
        <form className="settings-drawer-form" onSubmit={onSubmit} noValidate>
          <div className="settings-form-body">{children}</div>
          <footer className="settings-drawer-actions">
            <button className="button button-secondary" type="button" onClick={onClose} disabled={isPending}>Cancel</button>
            <button className="button button-primary" type="submit" disabled={isPending}>{isPending ? 'Saving...' : 'Save'}</button>
          </footer>
        </form>
      </aside>
    </div>
  );
};

const FormField = ({ label, required, full, error, children, hint, hideLabel }: {
  label: string;
  required?: boolean;
  full?: boolean;
  error?: string | undefined;
  children: ReactNode;
  hint?: string | undefined;
  hideLabel?: boolean;
}) => (
  <label className={`settings-field${full ? ' full' : ''}`}>
    {!hideLabel ? <span>{label}{required ? <b aria-hidden="true"> *</b> : null}</span> : null}
    {children}
    {hint ? <small className="settings-field-hint">{hint}</small> : null}
    {error ? <small className="settings-field-error">{error}</small> : null}
  </label>
);

const ChargeEditorPanel = ({ title, open, onToggle, children }: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => (
  <section className="settings-charge-panel">
    <button
      className="settings-charge-panel-toggle"
      type="button"
      aria-expanded={open}
      onClick={onToggle}
    >
      <span>{title}</span>
      <b aria-hidden="true">{open ? '-' : '+'}</b>
    </button>
    {open ? <div className="settings-charge-panel-body">{children}</div> : null}
  </section>
);

const emptyLookupExclusions: string[] = [];

const RemoteLookupSelect = ({
  resource,
  value,
  onChange,
  placeholder,
  parentId,
  initialLabel = '',
  excludedValues = emptyLookupExclusions,
}: {
  resource: 'charges' | 'charge-categories';
  value: string;
  onChange: (value: string, record?: SettingsRecord) => void;
  placeholder: string;
  parentId?: string;
  initialLabel?: string;
  excludedValues?: string[];
}) => {
  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState(initialLabel);
  const deferredSearch = useDeferredValue(search.trim());
  const query = useQuery({
    queryKey: [...settingsKeys.lookup(resource), 'remote', parentId ?? 'root', deferredSearch],
    queryFn: () => fetchSettingsLookup(resource, {
      ...(parentId ? { parentId } : {}),
      ...(deferredSearch ? { search: deferredSearch } : {}),
      limit: 100,
    }),
    enabled: parentId !== '',
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
  const records = useMemo(() => query.data ?? [], [query.data]);
  const options = useMemo(() => {
    const excluded = new Set(excludedValues);
    const nextOptions: SearchableSelectOption[] = [];
    if (value && !records.some((item) => item.id === value)) {
      nextOptions.push({ value, label: selectedLabel || 'Selected record' });
    }
    records.forEach((item) => {
      if (item.id !== value && excluded.has(item.id)) return;
      nextOptions.push({ value: item.id, label: lookupRecordLabel(item) });
    });
    return nextOptions;
  }, [excludedValues, records, selectedLabel, value]);

  useEffect(() => {
    setSearch('');
  }, [parentId, resource]);

  useEffect(() => {
    setSelectedLabel(initialLabel);
  }, [initialLabel]);

  const isDisabled = parentId === '' || (query.isLoading && !query.data);
  return (
    <div className="settings-remote-lookup settings-remote-lookup-single">
      <SearchableSelect
        value={value}
        options={options}
        placeholder={query.isLoading && !query.data ? 'Loading choices...' : placeholder}
        searchValue={search}
        searchPlaceholder={`Search ${placeholder.toLowerCase()}`}
        emptyMessage={query.isFetching ? 'Loading choices...' : 'No matches found'}
        disabled={isDisabled}
        clearable
        onSearchChange={setSearch}
        onChange={(nextValue) => {
          const nextRecord = records.find((item) => item.id === nextValue);
          if (nextRecord) setSelectedLabel(lookupRecordLabel(nextRecord));
          else if (!nextValue) setSelectedLabel('');
          onChange(nextValue, nextRecord);
        }}
      />
      {query.isError ? <small className="settings-field-error">Choices could not be loaded.</small> : null}
    </div>
  );
};

interface ParameterRow {
  label: '' | 'Child' | 'Group';
  groupCode: string;
  test: string;
  sequenceNumber: string;
  comments: string;
  unit: string;
  maleLower: string;
  maleUpper: string;
  femaleLower: string;
  femaleUpper: string;
  childLower: string;
  childUpper: string;
  method: string;
  instrumentUsed: string;
  lisCode: string;
  formula: string;
}

const emptyParameter = (): ParameterRow => ({
  label: '', groupCode: '', test: '', sequenceNumber: '', comments: '', unit: '',
  maleLower: '', maleUpper: '', femaleLower: '', femaleUpper: '', childLower: '', childUpper: '',
  method: '', instrumentUsed: '', lisCode: '', formula: '',
});

const recordParameters = (record?: SettingsRecord | null): ParameterRow[] => {
  if (!Array.isArray(record?.parameters)) return [];
  return record.parameters.map((raw) => {
    const item = typeof raw === 'object' && raw !== null ? raw as Record<string, unknown> : {};
    return {
      label: item.label === 'Group' ? 'Group' : item.label === 'Child' ? 'Child' : '',
      groupCode: textValue(item.groupCode),
      test: textValue(item.test),
      sequenceNumber: numberValue(item.sequenceNumber),
      comments: textValue(item.comments),
      unit: textValue(item.unit),
      maleLower: textValue(item.maleLower),
      maleUpper: textValue(item.maleUpper),
      femaleLower: textValue(item.femaleLower),
      femaleUpper: textValue(item.femaleUpper),
      childLower: textValue(item.childLower),
      childUpper: textValue(item.childUpper),
      method: textValue(item.method),
      instrumentUsed: textValue(item.instrumentUsed),
      lisCode: textValue(item.lisCode),
      formula: textValue(item.formula),
    };
  });
};

export const ChargeSettingsDrawer = ({ record, isPending, onClose, onSubmit }: EditorProps) => {
  const { showToast } = useToast();
  const [chargeType, setChargeType] = useState(textValue(record?.chargeType) || 'other-services');
  const [chargeSection] = useState(textValue(record?.chargeSection) || 'profile');
  const [category, setCategory] = useState(getReferenceId(record?.category));
  const [subCategory, setSubCategory] = useState(getReferenceId(record?.subCategory));
  const [name, setName] = useState(textValue(record?.name));
  const [requisitionSection, setRequisitionSection] = useState(getReferenceId(record?.requisitionSection));
  const [template, setTemplate] = useState(textValue(record?.template));
  const [note, setNote] = useState(textValue(record?.note));
  const [instrumentUsed, setInstrumentUsed] = useState(textValue(record?.instrumentUsed));
  const [sample, setSample] = useState(getReferenceId(record?.sample));
  const [sampleRequired, setSampleRequired] = useState(textValue(record?.sampleRequired));
  const [vial, setVial] = useState(getReferenceId(record?.vial));
  const [parameters, setParameters] = useState(() => {
    const existing = recordParameters(record);
    return existing.length ? [...existing, emptyParameter()] : [emptyParameter()];
  });
  const [normalOpen, setNormalOpen] = useState(true);
  const [templateOpen, setTemplateOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const initialRates = useMemo(() => {
    const result: Record<string, string> = {};
    if (Array.isArray(record?.sectionRates)) {
      record.sectionRates.forEach((raw) => {
        if (typeof raw !== 'object' || raw === null) return;
        const item = raw as Record<string, unknown>;
        const sectionId = getReferenceId(item.section);
        if (sectionId) result[sectionId] = typeof item.amount === 'number' ? String(item.amount) : '';
      });
    }
    return result;
  }, [record]);
  const [sectionRates, setSectionRates] = useState<Record<string, string>>(initialRates);

  const chargeSections = useSettingsLookup('charge-sections', true);
  const requisitionSections = useSettingsLookup('requisition-sections', true);
  const samples = useSettingsLookup('test-samples', true);
  const vials = useSettingsLookup('vials', true);

  const updateParameter = (index: number, field: keyof ParameterRow, value: string) => {
    setParameters((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row));
    setErrors((current) => ({ ...current, [`parameter-${index}`]: '' }));
  };

  const parameterHasContent = (parameter: ParameterRow) => [
    parameter.groupCode,
    parameter.test,
    parameter.sequenceNumber,
    parameter.comments,
    parameter.unit,
    parameter.maleLower,
    parameter.maleUpper,
    parameter.femaleLower,
    parameter.femaleUpper,
    parameter.childLower,
    parameter.childUpper,
    parameter.method,
    parameter.instrumentUsed,
    parameter.lisCode,
    parameter.formula,
  ].some((value) => value.trim() !== '');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!category) nextErrors.category = 'Category is required.';
    if (!subCategory) nextErrors.subCategory = 'Sub-Category is required.';
    if (!name.trim()) nextErrors.name = 'Charge Name is required.';
    const selectedRates = Object.entries(sectionRates).filter(([, amount]) => amount !== '');
    if (!selectedRates.length) nextErrors.sectionRates = 'Select at least one Charges Section and enter its amount.';
    if (selectedRates.some(([, amount]) => !Number.isFinite(Number(amount)) || Number(amount) < 0)) {
      nextErrors.sectionRates = 'Every selected section amount must be zero or greater.';
    }
    parameters.forEach((parameter, index) => {
      if (!parameterHasContent(parameter)) return;
      if (!parameter.label) nextErrors[`parameter-${index}`] = 'Label is required for this parameter.';
      else if (!parameter.test.trim()) nextErrors[`parameter-${index}`] = 'Test Parameter is required for this row.';
      if (parameter.sequenceNumber && (!Number.isInteger(Number(parameter.sequenceNumber)) || Number(parameter.sequenceNumber) < 0)) {
        nextErrors[`parameter-${index}`] = 'Sequence No. must be a whole number of zero or greater.';
      }
    });
    if (Object.keys(nextErrors).length) { setErrors(nextErrors); return; }

    const parameterPayload = parameters.filter(parameterHasContent).map((parameter) => ({
      label: parameter.label,
      groupCode: optionalText(parameter.groupCode),
      test: parameter.test.trim(),
      sequenceNumber: parameter.sequenceNumber === '' ? null : Number(parameter.sequenceNumber),
      comments: optionalText(parameter.comments),
      unit: optionalText(parameter.unit),
      maleLower: optionalText(parameter.maleLower),
      maleUpper: optionalText(parameter.maleUpper),
      femaleLower: optionalText(parameter.femaleLower),
      femaleUpper: optionalText(parameter.femaleUpper),
      childLower: optionalText(parameter.childLower),
      childUpper: optionalText(parameter.childUpper),
      method: optionalText(parameter.method),
      instrumentUsed: optionalText(parameter.instrumentUsed),
      lisCode: optionalText(parameter.lisCode),
      formula: optionalText(parameter.formula),
    }));
    try {
      await onSubmit({
        chargeType,
        chargeSection,
        department: null,
        category,
        subCategory,
        name: name.trim(),
        requisitionSection: requisitionSection || null,
        sectionRates: selectedRates.map(([section, amount]) => ({ section, amount: Number(amount) })),
        parameters: parameterPayload,
        template: optionalText(template),
        note: optionalText(note),
        instrumentUsed: optionalText(instrumentUsed),
        sample: sample || null,
        sampleRequired: optionalText(sampleRequired),
        vial: vial || null,
      });
      showToast('Charge saved successfully.', 'success');
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'The charge could not be saved.', 'error');
    }
  };

  return (
    <Drawer title={record ? 'Edit charge' : 'Add charge'} eyebrow={record ? 'Edit configuration' : 'New configuration'} isPending={isPending} onClose={onClose} onSubmit={(event) => void submit(event)}>
      <section className="settings-charge-basics" aria-label="Charge details">
        <div className="settings-charge-basics-grid">
          <FormField label="#" required><select value={chargeType} onChange={(event) => setChargeType(event.target.value)}><option value="other-services">Other Services</option><option value="investigation">Investigation</option></select></FormField>
          <FormField label="Charges Name" required hideLabel error={errors.name}><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Charges Name" aria-label="Charges Name" /></FormField>
          <FormField label="Charge Category" required error={errors.category}><RemoteLookupSelect resource="charge-categories" value={category} onChange={(value) => { setCategory(value); setSubCategory(''); }} placeholder="Select category" initialLabel={record?.category && typeof record.category === 'object' ? lookupRecordLabel(record.category as SettingsRecord) : ''} /></FormField>
          <FormField label="Charges Sub Category" required error={errors.subCategory}><RemoteLookupSelect resource="charge-categories" parentId={category} value={subCategory} onChange={setSubCategory} placeholder="Select sub-category" initialLabel={record?.subCategory && typeof record.subCategory === 'object' ? lookupRecordLabel(record.subCategory as SettingsRecord) : ''} /></FormField>
          <FormField label="Requisition Section"><select value={requisitionSection} onChange={(event) => setRequisitionSection(event.target.value)} disabled={requisitionSections.isLoading}><option value="">Select requisition section</option>{requisitionSections.data?.map((item) => <option key={item.id} value={item.id}>{lookupRecordLabel(item)}</option>)}</select></FormField>
        </div>
      </section>

      <section className="settings-charge-rates" aria-label="Charge section amounts">
        <div className="settings-rate-list">
          {chargeSections.isLoading ? <p>Loading charge sections...</p> : chargeSections.data?.map((section) => {
            const selected = Object.hasOwn(sectionRates, section.id);
            const sectionLabel = lookupRecordLabel(section);
            return (
              <div className="settings-rate-item" key={section.id}>
                <strong>{sectionLabel}</strong>
                <div className="settings-rate-row">
                  <label className="settings-rate-check" aria-label={`Enable ${sectionLabel}`}><input type="checkbox" checked={selected} onChange={(event) => setSectionRates((current) => {
                    if (event.target.checked) return { ...current, [section.id]: '0' };
                    const next = { ...current }; delete next[section.id]; return next;
                  })} /></label>
                  <input type="number" min="0" step="0.01" aria-label={`${sectionLabel} amount`} disabled={!selected} value={sectionRates[section.id] ?? '0'} onChange={(event) => setSectionRates((current) => ({ ...current, [section.id]: event.target.value }))} />
                </div>
              </div>
            );
          })}
        </div>
        {errors.sectionRates ? <small className="settings-field-error">{errors.sectionRates}</small> : null}
      </section>

      <ChargeEditorPanel title="Normal" open={normalOpen} onToggle={() => setNormalOpen((current) => !current)}>
        <div className="settings-parameter-table-scroll">
          <table className="settings-parameter-table">
            <thead>
              <tr>
                <th>Label</th><th>Grp.Cd</th><th>Test Parameter</th><th>Seq No.</th><th>#</th><th>Comments</th><th>Unit</th>
                <th>M/LL</th><th>M/UL</th><th>F/LL</th><th>F/UL</th><th>C/LL</th><th>C/UL</th><th>Method</th>
                <th>Instrument Used</th><th>LIS Code</th><th>Formula</th>
              </tr>
            </thead>
            <tbody>
              {parameters.map((parameter, index) => (
                <tr key={index}>
                  <td><select aria-label={`Parameter ${index + 1} label`} value={parameter.label} onChange={(event) => updateParameter(index, 'label', event.target.value)}><option value="">Select</option><option value="Child">CHLd</option><option value="Group">Grp.</option></select></td>
                  <td><input aria-label={`Parameter ${index + 1} group code`} value={parameter.groupCode} onChange={(event) => updateParameter(index, 'groupCode', event.target.value)} /></td>
                  <td className="settings-parameter-test-cell"><input aria-label={`Parameter ${index + 1} test parameter`} aria-invalid={Boolean(errors[`parameter-${index}`])} value={parameter.test} onChange={(event) => updateParameter(index, 'test', event.target.value)} />{errors[`parameter-${index}`] ? <small className="settings-field-error">{errors[`parameter-${index}`]}</small> : null}</td>
                  <td><input type="number" min="0" step="1" aria-label={`Parameter ${index + 1} sequence number`} value={parameter.sequenceNumber} onChange={(event) => updateParameter(index, 'sequenceNumber', event.target.value)} /></td>
                  <td><div className="settings-parameter-actions"><button type="button" title="Add parameter row" aria-label={`Add row after parameter ${index + 1}`} onClick={() => setParameters((current) => [...current.slice(0, index + 1), emptyParameter(), ...current.slice(index + 1)])}>+</button>{parameters.length > 1 || parameterHasContent(parameter) ? <button type="button" title="Remove parameter row" aria-label={`Remove parameter ${index + 1}`} onClick={() => setParameters((current) => current.length === 1 ? [emptyParameter()] : current.filter((_, rowIndex) => rowIndex !== index))}>-</button> : null}</div></td>
                  <td><input aria-label={`Parameter ${index + 1} comments`} value={parameter.comments} onChange={(event) => updateParameter(index, 'comments', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} unit`} value={parameter.unit} onChange={(event) => updateParameter(index, 'unit', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} male lower limit`} value={parameter.maleLower} onChange={(event) => updateParameter(index, 'maleLower', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} male upper limit`} value={parameter.maleUpper} onChange={(event) => updateParameter(index, 'maleUpper', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} female lower limit`} value={parameter.femaleLower} onChange={(event) => updateParameter(index, 'femaleLower', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} female upper limit`} value={parameter.femaleUpper} onChange={(event) => updateParameter(index, 'femaleUpper', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} child lower limit`} value={parameter.childLower} onChange={(event) => updateParameter(index, 'childLower', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} child upper limit`} value={parameter.childUpper} onChange={(event) => updateParameter(index, 'childUpper', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} method`} value={parameter.method} onChange={(event) => updateParameter(index, 'method', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} instrument used`} value={parameter.instrumentUsed} onChange={(event) => updateParameter(index, 'instrumentUsed', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} LIS code`} value={parameter.lisCode} onChange={(event) => updateParameter(index, 'lisCode', event.target.value)} /></td>
                  <td><input aria-label={`Parameter ${index + 1} formula`} value={parameter.formula} onChange={(event) => updateParameter(index, 'formula', event.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChargeEditorPanel>

      <ChargeEditorPanel title="Default Template" open={templateOpen} onToggle={() => setTemplateOpen((current) => !current)}>
        <CkEditorField ariaLabel="Default Template editor" className="settings-charge-template-editor" value={template} onChange={setTemplate} />
      </ChargeEditorPanel>

      <section className="settings-charge-details" aria-label="Additional charge details">
        <div className="settings-charge-details-grid">
          <label className="settings-field"><CkEditorField label="Note" value={note} onChange={setNote} /></label>
          <label className="settings-field"><CkEditorField label="Instrument Used" value={instrumentUsed} onChange={setInstrumentUsed} /></label>
          <FormField label="Sample"><select value={sample} onChange={(event) => setSample(event.target.value)} disabled={samples.isLoading}><option value="">Select sample</option>{samples.data?.map((item) => <option key={item.id} value={item.id}>{lookupRecordLabel(item)}</option>)}</select></FormField>
          <FormField label="Sample Required"><input value={sampleRequired} onChange={(event) => setSampleRequired(event.target.value)} /></FormField>
          <FormField label="Vial"><select value={vial} onChange={(event) => setVial(event.target.value)} disabled={vials.isLoading}><option value="">Select vial</option>{vials.data?.map((item) => <option key={item.id} value={item.id}>{lookupRecordLabel(item)}</option>)}</select></FormField>
        </div>
      </section>
    </Drawer>
  );
};

interface PackageItemValue { charge: string; rate: string; chargeLabel: string }

export const PackageSettingsDrawer = ({ record, isPending, onClose, onSubmit }: EditorProps) => {
  const { showToast } = useToast();
  const [packageName, setPackageName] = useState(textValue(record?.packageName));
  const [items, setItems] = useState<PackageItemValue[]>(() => {
    if (!Array.isArray(record?.items) || !record.items.length) return [{ charge: '', rate: '', chargeLabel: '' }];
    return record.items.map((raw) => {
      const item = typeof raw === 'object' && raw !== null ? raw as Record<string, unknown> : {};
      return {
        charge: getReferenceId(item.charge),
        rate: numberValue(item.rate),
        chargeLabel: item.charge && typeof item.charge === 'object' ? lookupRecordLabel(item.charge as SettingsRecord) : '',
      };
    });
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const totalAmount = items.reduce((sum, item) => sum + (Number.isFinite(Number(item.rate)) ? Number(item.rate) : 0), 0);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!packageName.trim()) nextErrors.packageName = 'Package Name is required.';
    items.forEach((item, index) => {
      if (!item.charge) nextErrors[`item-${index}`] = 'Charge is required.';
      else if (item.rate === '' || !Number.isFinite(Number(item.rate)) || Number(item.rate) < 0) nextErrors[`item-${index}`] = 'Rate must be zero or greater.';
    });
    const selectedCharges = items.map((item) => item.charge).filter(Boolean);
    if (new Set(selectedCharges).size !== selectedCharges.length) nextErrors.items = 'Each charge may be added only once.';
    if (Object.keys(nextErrors).length) { setErrors(nextErrors); return; }
    try {
      await onSubmit({
        packageName: packageName.trim(),
        items: items.map((item) => ({ charge: item.charge, rate: Number(item.rate) })),
        totalAmount,
      });
      showToast('Package saved successfully.', 'success');
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'The package could not be saved.', 'error');
    }
  };

  return (
    <Drawer title={record ? 'Edit package' : 'Add package'} eyebrow={record ? 'Edit configuration' : 'New configuration'} isPending={isPending} onClose={onClose} onSubmit={(event) => void submit(event)}>
      <fieldset className="settings-form-section">
        <legend>Package</legend>
        <div className="settings-form-grid">
          <FormField label="Package Name" required full error={errors.packageName}><input value={packageName} onChange={(event) => setPackageName(event.target.value)} /></FormField>
        </div>
      </fieldset>
      <fieldset className="settings-form-section">
        <legend>Charges</legend>
        {errors.items ? <div className="settings-field-error settings-repeat-error">{errors.items}</div> : null}
        <div className="settings-repeat-list">
          {items.map((item, index) => (
            <section className="settings-package-row" key={index}>
              <FormField label="Charge" required error={errors[`item-${index}`]}><RemoteLookupSelect resource="charges" value={item.charge} initialLabel={item.chargeLabel} excludedValues={items.filter((_, rowIndex) => rowIndex !== index).map((row) => row.charge)} onChange={(value, selected) => setItems((current) => current.map((row, rowIndex) => rowIndex === index ? {
                ...row,
                charge: value,
                chargeLabel: selected ? lookupRecordLabel(selected) : row.chargeLabel,
                rate: selected && typeof selected.rate === 'number' ? String(selected.rate) : row.rate,
              } : row))} placeholder="Select charge" /></FormField>
              <FormField label="Rate" required><input type="number" min="0" step="0.01" value={item.rate} onChange={(event) => setItems((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, rate: event.target.value } : row))} /></FormField>
              <button type="button" className="settings-remove-row" disabled={items.length === 1} onClick={() => setItems((current) => current.filter((_, rowIndex) => rowIndex !== index))}>Remove</button>
            </section>
          ))}
        </div>
        <button type="button" className="settings-add-row" onClick={() => setItems((current) => [...current, { charge: '', rate: '', chargeLabel: '' }])}>+ Add Charge</button>
        <label className="settings-package-total"><span>Total Amount</span><output>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(totalAmount)}</output></label>
      </fieldset>
    </Drawer>
  );
};

const HEADER_IMAGE_MAX_BYTES = 500 * 1024;
const headerImageTypes = ['image/png', 'image/jpeg'];

export const DocumentHeaderSettingsDrawer = ({ record, isPending, onClose, onSubmit }: EditorProps) => {
  const { showToast } = useToast();
  const [headerName, setHeaderName] = useState(textValue(record?.headerName));
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(textValue(record?.logoDataUrl) || null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isReadingImage, setIsReadingImage] = useState(false);

  const chooseImage = (file?: File) => {
    if (!file) return;
    if (!headerImageTypes.includes(file.type)) {
      setErrors((current) => ({ ...current, logoDataUrl: 'Choose a PNG, JPG, or JPEG image.' }));
      return;
    }
    if (file.size > HEADER_IMAGE_MAX_BYTES) {
      setErrors((current) => ({ ...current, logoDataUrl: 'The image must be 500 KB or smaller.' }));
      return;
    }
    setIsReadingImage(true);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setLogoDataUrl(reader.result);
        setErrors((current) => ({ ...current, logoDataUrl: '' }));
      }
      setIsReadingImage(false);
    };
    reader.onerror = () => {
      setIsReadingImage(false);
      setErrors((current) => ({ ...current, logoDataUrl: 'The image could not be read.' }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isReadingImage) return;
    const nextErrors: Record<string, string> = {};
    if (!headerName.trim()) nextErrors.headerName = 'Header Name is required.';
    if (!record && !logoDataUrl) nextErrors.logoDataUrl = 'Logo is required.';
    if (Object.keys(nextErrors).length) { setErrors(nextErrors); return; }
    try {
      await onSubmit({ headerName: headerName.trim(), logoDataUrl });
      showToast('Header saved successfully.', 'success');
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'The header could not be saved.', 'error');
    }
  };

  return (
    <Drawer title={record ? 'Edit header' : 'Add header'} eyebrow={record ? 'Edit configuration' : 'New configuration'} isPending={isPending || isReadingImage} onClose={onClose} onSubmit={(event) => void submit(event)}>
      <fieldset className="settings-form-section">
        <legend>Header</legend>
        <div className="settings-form-grid">
          <FormField label="Header Name" required full error={errors.headerName} hint={'Print pages match this name exactly, e.g. "IPD Admission Form".'}><input value={headerName} onChange={(event) => setHeaderName(event.target.value)} placeholder="IPD Admission Form" /></FormField>
          <FormField label="Logo (245px x 48px)" required full error={errors.logoDataUrl} hint="PNG, JPG, or JPEG; maximum file size 500 KB.">
            <input type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg" onChange={(event) => chooseImage(event.target.files?.[0])} />
          </FormField>
          {logoDataUrl ? (
            <div className="settings-header-preview">
              <img src={logoDataUrl} alt="Header logo preview" />
              <button type="button" className="settings-remove-row" onClick={() => setLogoDataUrl(null)}>Clear logo</button>
            </div>
          ) : <div className="settings-header-preview-empty">No logo selected</div>}
        </div>
      </fieldset>
    </Drawer>
  );
};
